import React, { useState } from 'react';
import './AnimationSettingsSection.css';
import ToggleSwitch from './ToggleSwitch';
import SliderSection from './SliderSection';

/**
 * 🎬 UNIVERSAL ANIMATION SETTINGS SECTION 2025
 * 
 * Universelle Animation-Komponente für alle Widgets:
 * - Enable/Disable Toggle
 * - Animation Type Selector (Visual Grid)
 * - Animation Speed/Duration Controls
 * - Animation Delay Controls
 * - Konsistentes Design mit anderen Sections
 * 
 * Usage:
 * <AnimationSettingsSection 
 *     animationTypes={customAnimationTypes} // Optional: Custom animation types
 *     editData={editData}
 *     onUpdate={onUpdate}
 *     showSpeedControls={true} // Optional: Show speed/delay controls
 *     showDelayControls={true} // Optional: Show delay controls
 *     defaultExpanded={false} // Optional: Start expanded
 * />
 */
function AnimationSettingsSection({ 
    animationTypes = null, // Custom animation types, falls null = default types
    editData, 
    onUpdate,
    showSpeedControls = true,
    showDelayControls = true,
    defaultExpanded = false,
    enableFieldName = 'enableAnimations', // Customizable field name
    typeFieldName = 'animationType', // Customizable field name
    durationFieldName = 'animationDuration', // Customizable field name
    delayFieldName = 'animationDelay', // Customizable field name
    triggerFieldName = 'triggerAnimation' // Customizable field name
}) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Default Animation Types - Core 4 Animations
    const defaultAnimationTypes = [
        {
            id: 'fadeInUp',
            name: 'Fade In Up',
            icon: '↗️',
            description: 'Smooth fade in with upward movement'
        },
        {
            id: 'slideIn',
            name: 'Slide In',
            icon: '➡️',
            description: 'Slide in from the side'
        },
        {
            id: 'fadeIn',
            name: 'Fade In',
            icon: '✨',
            description: 'Simple fade in effect'
        },
        {
            id: 'fadeInSlow',
            name: 'Fade Slow',
            icon: '🌙',
            description: 'Slow and elegant fade in'
        }
    ];

    // Use custom animation types or default ones
    const availableAnimations = animationTypes || defaultAnimationTypes;

    // Handle animation type change
    const handleAnimationTypeChange = (animationType) => {
        const updatedData = { 
            ...editData, 
            [typeFieldName]: animationType,
            [triggerFieldName]: Date.now() // Trigger animation preview
        };
        onUpdate(updatedData);
    };

    // Handle toggle change
    const handleToggleChange = (e) => {
        const { checked } = e.target;
        const updatedData = { 
            ...editData, 
            [enableFieldName]: checked 
        };
        onUpdate(updatedData);
    };

    // Handle slider change for duration/delay
    const handleSliderChange = (fieldName, value) => {
        const updatedData = { 
            ...editData, 
            [fieldName]: parseInt(value) || 0 
        };
        onUpdate(updatedData);
    };

    return (
        <div className="animation-settings-section">
            {/* Toggle Header */}
            <div 
                className={`animation-settings-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="animation-settings-header-content">
                    <div className="animation-settings-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1L10 5H14L11 8L12 12L8 10L4 12L5 8L2 5H6L8 1Z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1" fill="none"/>
                            <path d="M6 8L10 8M8 6L8 10" stroke="currentColor" strokeWidth="0.8"/>
                        </svg>
                    </div>
                    <h3 className="animation-settings-header-title">Animations</h3>
                    <div className="animation-settings-status">
                        {editData[enableFieldName] ? 'ON' : 'OFF'}
                    </div>
                </div>
                <div className={`animation-settings-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="animation-settings-content">
                    
                    {/* Enable/Disable Toggle */}
                    <div className="animation-enable-section">
                        <ToggleSwitch
                            label="Enable Animations"
                            fieldName={enableFieldName}
                            checked={editData[enableFieldName] || false}
                            onChange={handleToggleChange}
                            color="#8b5cf6"
                            size="medium"
                        />
                        <div className="animation-enable-hint">
                            Turn on animations to make your widget come alive
                        </div>
                    </div>

                    {/* Animation Types Grid - Only show if animations enabled */}
                    {editData[enableFieldName] && (
                        <>
                            <div className="animation-types-section">
                                <h5 className="animation-types-label">Animation Style</h5>
                                
                                <div className="animation-types-grid">
                                    {availableAnimations.map((animation) => (
                                        <div
                                            key={animation.id}
                                            className={`animation-type-option ${
                                                editData[typeFieldName] === animation.id ? 'selected' : ''
                                            }`}
                                            onClick={() => handleAnimationTypeChange(animation.id)}
                                            title={animation.description}
                                        >
                                            <div className="animation-type-icon">
                                                {animation.icon}
                                            </div>
                                            <span className="animation-type-name">
                                                {animation.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Animation Controls */}
                            {(showSpeedControls || showDelayControls) && (
                                <div className="animation-controls-section">
                                    <h5 className="animation-controls-label">Animation Settings</h5>
                                    
                                    <div className="animation-controls-grid">
                                        {showSpeedControls && (
                                            <div className="animation-control-item">
                                                <SliderSection
                                                    label="Animation Speed"
                                                    fieldName={durationFieldName}
                                                    value={editData[durationFieldName] || 600}
                                                    min={200}
                                                    max={2000}
                                                    step={100}
                                                    unit="ms"
                                                    onChange={handleSliderChange}
                                                    color="#8b5cf6"
                                                    description="Duration of the animation effect"
                                                    icon="/img/element.svg"
                                                />
                                            </div>
                                        )}
                                        
                                        {showDelayControls && (
                                            <div className="animation-control-item">
                                                <SliderSection
                                                    label="Animation Delay"
                                                    fieldName={delayFieldName}
                                                    value={editData[delayFieldName] || 200}
                                                    min={0}
                                                    max={1000}
                                                    step={50}
                                                    unit="ms"
                                                    onChange={handleSliderChange}
                                                    color="#a855f7"
                                                    description="Delay before animation starts"
                                                    icon="/img/element.svg"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Animation Preview Info */}
                            <div className="animation-preview-info">
                                <div className="animation-preview-icon">
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1" fill="none"/>
                                        <path d="M7 3V7L10 10" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <span className="animation-preview-text">
                                    Animations will play when the widget comes into view
                                </span>
                            </div>
                        </>
                    )}

                    {/* Empty State when animations disabled */}
                    {!editData[enableFieldName] && (
                        <div className="animation-empty-state">
                            <div className="animation-empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" opacity="0.3"/>
                                    <path d="M24 14L26 22H32L27 26L28 34L24 30L20 34L21 26L16 22H22L24 14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                                    <circle cx="24" cy="24" r="3" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
                                </svg>
                            </div>
                            <h4>Animations Disabled</h4>
                            <p>Enable animations above to bring your widget to life with smooth entrance effects</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AnimationSettingsSection;
